@if (blocked()) {
  <main class="gate">
    <section class="gate-card" role="alert">
      <p class="gate-app">
        Solvind Dashboard
        <span class="env" [class.production]="environmentName === 'Production'">{{
          environmentName
        }}</span>
      </p>
      <strong class="gate-title">{{ errorTitle() }}</strong>
      <p class="gate-message">{{ error() }}</p>
      @switch (errorKind()) {
        @case ('forbidden') {
          <button type="button" class="retry" (click)="auth.signOut()">Sign out</button>
        }
        @case ('rejected') {
          <button type="button" class="retry" (click)="auth.signIn()">Sign in again</button>
        }
        @default {
          <button type="button" class="retry" (click)="auth.signIn()">Try again</button>
        }
      }
    </section>
  </main>
} @else {
  <header class="topbar" #topbar>
    <div class="heading">
      <h1>
        {{ pageTitle() }}
        <span class="env" [class.production]="environmentName === 'Production'">{{
          environmentName
        }}</span>
      </h1>
      @switch (tab()) {
        @case ('service-bus') {
          <p class="subtitle">
            Messages that Service Bus could not deliver or process and put aside, for each queue and
            topic we watch. We email when one gets its first dead letter.
          </p>
        }
        @case ('application-insights') {
          <p class="subtitle">
            How each running part of our applications is doing, judged by the requests it receives,
            the calls it makes to other services and its exceptions.
          </p>
        }
        @default {
          <p class="subtitle">
            The health of our systems in one place: what is wrong right now, and how each source is
            doing.
          </p>
        }
      }
      @if (headerSummary(); as headline) {
        <div class="summary" [class]="'summary ' + headline.tone" role="status">
          <strong>{{ headline.headline }}</strong>
          <ul>
            @for (part of headline.parts; track part.label) {
              <li [class]="part.tone">{{ part.count | number }} {{ part.label }}</li>
            }
          </ul>
        </div>
      }
      @if (tab() === 'service-bus') {
        <details class="how">
          <summary>What are dead letters?</summary>
          <ul>
            <li>
              <strong>Dead-letter queue.</strong> Every queue, and every subscription of a topic,
              has a second queue for messages that could not be delivered or processed, for example
              because they were rejected too many times or expired. They stay there until someone
              inspects, resends or deletes them.
            </li>
            <li>
              <strong>Topics.</strong> A topic has no dead-letter queue of its own, so the figures
              of a topic add up its subscriptions, and the card lists them one by one.
            </li>
            <li>
              <strong>Read-only.</strong> The dashboard only reads the message counts from Azure. It
              never receives, locks or changes a message.
            </li>
            <li>
              <strong>Email.</strong> One email goes out when a queue or subscription gets its first
              dead letter, and one when it is empty again. More messages arriving in the meantime do
              not send more emails.
            </li>
          </ul>
        </details>
      } @else if (healthRules(); as r) {
        <details class="how">
          <summary>How is health calculated?</summary>
          <ul>
            <li>
              <strong>Requests and calls.</strong> We look at the latest
              {{ r.requestCount }} requests the app received and the latest
              {{ r.requestCount }} calls it made to other services of each kind ({{
                r.callTypes.join(', ')
              }}). A failed call to another service always counts as a failure. A request that
              failed because the caller did something wrong (a 4xx code) does not.
            </li>
            <li>
              <strong>Exceptions.</strong> Errors thrown inside the app in the last
              {{ r.exceptionWindowMinutes }} minutes. {{ r.exceptionDegradedCount }} or more makes
              the status Degraded and {{ r.exceptionUnhealthyCount }} or more makes it Unhealthy,
              even if requests look fine.
            </li>
            <li>
              <strong>Status.</strong> Each kind of request or call (incoming requests, and every
              type of call above) is judged on its own, so a failing one is never hidden by healthy
              ones. Healthy: {{ r.healthyThreshold }}% or more succeeded. Degraded:
              {{ r.degradedThreshold }}% up to {{ r.healthyThreshold }}%. Unhealthy: below
              {{ r.degradedThreshold }}%. The worst kind and the exceptions decide the status.
            </li>
            <li>
              <strong>No data.</strong> Nothing was received or called recently, so there is nothing
              to judge. Background workers often look like this until they raise exceptions.
            </li>
            <li>
              <strong>Several cards for one application.</strong> Each running part (a role, such as
              an API or a worker) is judged on its own, even when they share one Application
              Insights resource.
            </li>
          </ul>
          <p>The data is refreshed every {{ r.refreshSeconds }} seconds.</p>
        </details>
      }
    </div>
    <div class="refresh-area">
      @if (shownUpdated(); as updated) {
        <span class="updated" [attr.title]="updated | date: 'medium'"
          >Updated {{ shownUpdatedAgo() }}</span
        >
      }
      <button type="button" class="refresh" (click)="refresh()" [disabled]="refreshing()">
        {{ refreshing() ? 'Refreshing…' : 'Refresh' }}
      </button>
      @if (auth.account()) {
        <span class="user" [attr.title]="auth.account()?.username">{{ auth.userName() }}</span>
        <button type="button" class="signout" (click)="auth.signOut()">Sign out</button>
      }
    </div>
  </header>

  <nav class="tabs" aria-label="Dashboard sections">
    <a class="tab" routerLink="/" [attr.aria-current]="tab() === 'overview' ? 'page' : null"
      >Overview</a
    >
    <a
      class="tab"
      routerLink="/application-insights"
      [attr.aria-current]="tab() === 'application-insights' ? 'page' : null"
    >
      Application Insights
      @if (unhealthyCount() > 0) {
        <span class="tab-count"
          >{{ unhealthyCount() }}<span class="visually-hidden"> unhealthy</span></span
        >
      }
    </a>
    <a
      class="tab"
      routerLink="/service-bus"
      [attr.aria-current]="tab() === 'service-bus' ? 'page' : null"
    >
      Service Bus
      @if (deadLetteredCount() > 0) {
        <span class="tab-count"
          >{{ deadLetteredCount() }}<span class="visually-hidden"> with dead letters</span></span
        >
      }
    </a>
  </nav>

  <main>
    @if (tab() === 'overview') {
      <app-overview
        [sources]="sources()"
        [attention]="attention()"
        [now]="now()"
        (open)="openFromOverview($event)"
      />
    } @else if (tab() === 'service-bus') {
      @if (serviceBusError(); as message) {
        <app-banner
          heading="The Service Bus data could not be loaded."
          [message]="message"
          [note]="serviceBusBannerNote()"
          [busy]="serviceBusLoading()"
          (retry)="refresh()"
        />
      }

      @if (serviceBusLoading() && serviceBus().length === 0 && !serviceBusError()) {
        <p class="visually-hidden" role="status">Loading Service Bus…</p>
        <div class="grid" aria-hidden="true">
          @for (placeholder of skeletons; track placeholder) {
            <div class="skeleton">
              <span class="bone title"></span>
              <span class="bone short"></span>
              <span class="bone long"></span>
              <span class="bone bar"></span>
            </div>
          }
        </div>
      } @else if (serviceBus().length === 0 && !serviceBusError()) {
        <p class="state">No Service Bus queues or topics are configured on the backend.</p>
      } @else if (serviceBus().length > 0) {
        <app-service-bus
          [entities]="serviceBus()"
          [now]="now()"
          [staleAfterMs]="serviceBusStaleAfterMs()"
        />
      }
    } @else {
      @if (error(); as message) {
        <app-banner
          [heading]="errorTitle()"
          [message]="message"
          [note]="bannerNote()"
          [busy]="loading()"
          (retry)="refresh()"
        />
      }

      @if (loading() && apps().length === 0 && !error()) {
        <p class="visually-hidden" role="status">Loading apps…</p>
        <div class="grid" aria-hidden="true">
          @for (placeholder of skeletons; track placeholder) {
            <div class="skeleton">
              <span class="bone title"></span>
              <span class="bone short"></span>
              <span class="bone long"></span>
              <span class="bone bar"></span>
              <span class="bone bar"></span>
            </div>
          }
        </div>
      } @else if (apps().length === 0 && !error()) {
        <p class="state">No Application Insights apps are configured on the backend.</p>
      } @else if (apps().length > 0) {
        <app-filter-bar
          searchLabel="Search by name"
          [chips]="chips()"
          [allCount]="matchCount()"
          [allPressed]="selected().size === 0"
          [selects]="selects()"
          [views]="viewOptions"
          [view]="view()"
          [shown]="visibleApps().length"
          [total]="apps().length"
          [hasFilter]="hasFilter()"
          [compactSummary]="compactSummary()"
          [(query)]="query"
          (toggleChip)="toggleFilter($event)"
          (clearChips)="clearFilter()"
          (clearAll)="clearAll()"
          (selectChange)="changeSelect($event)"
          (viewChange)="changeView($event)"
        />

        @if (visibleApps().length === 0) {
          <p class="state">
            No cards match your search or filter.
            <button type="button" class="link" (click)="clearAll()">Clear filters</button>
          </p>
        } @else if (view() === 'list') {
          <div class="list" role="list" aria-label="Applications">
            @for (app of visibleApps(); track app.appId + '|' + app.roleName) {
              <app-health-row [app]="app" [now]="now()" [staleAfterMs]="staleAfterMs()" />
            }
          </div>
        } @else {
          <div class="grid">
            @for (app of visibleApps(); track app.appId + '|' + app.roleName) {
              <app-health-card [app]="app" [now]="now()" [staleAfterMs]="staleAfterMs()" />
            }
          </div>
        }
      }
    }
  </main>
}
